/**
 * 大分県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「大分県は鶏肉消費量日本一」は裏が取れない。家計調査のランキングは都道府県別ではなく
 *    県庁所在市別で、県公式のウェブマガジン自身が「大分市は4位、1位は福岡市」と書いた年がある。
 *    農水省の「全国トップレベル」に留める。
 *  - 「中津はからあげ店の数日本一」「からあげの聖地」は中津市公式にどちらも記載がない。
 *  - 中津からあげの発祥は市公式が複数の説を並記している。一つに絞らない。
 *  - とり天の発祥店は農水省が店名を挙げているが、本作は店名を書かない。発祥年も「昭和初期」まで。
 *  - りゅうきゅう・ひゅうが丼・石垣もち・ごまだしの名前の由来は、いずれも出典が
 *    複数説を併記するか「不明」と明記している。
 *  - かぼすのシェアは県公式「97%」と九州農政局「9割以上」で違う。幅のある表現にする。
 *  - 関あじ・関さばは「大分県産」ではない。大分市佐賀関沖の速吸瀬戸で獲られ、
 *    佐賀関に水揚げされたものだけの呼び名。
 *  - 豊後牛と「おおいた豊後牛」「うまみの証」は別の認証区分。料理名には広い呼称を使い、
 *    認証名を説明に混ぜない。また県公式は調理法を特定していないので、
 *    「すき焼き」「しゃぶしゃぶ」と断定しない。
 *
 * 本番画像は public/assets/foods/oita/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：かぼす・石垣もち（店で食べられる品に差し替え）
 * - 別府冷麺：始まりの店・年は公式ページで確かめられなかったので書かない
 * - 日田焼きそば：「元祖」や考案した店・年は店の自称で公式の裏付けがないので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { OITA_FOOD_HULLS } from './oitaFoodShapes.generated';
import { oitaSceneryTour } from './oitaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'yaseuma',
    level: 1,
    name: 'やせうま',
    shortName: 'やせうま',
    image: '/assets/foods/oita/01_yaseuma.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '大分の粉食は、ここから。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/yaseuma_oita.html',
    icon: { label: 'やせうま', shape: 'mochi', baseColor: '#f0e8d0', accentColor: '#c8a050' },
    reward: {
      unlockScore: 200,
      area: '由布市ほか県内全域',
      headline: '小麦粉を平たくのばしてゆで、きなこと砂糖をまぶした素朴な菓子。',
      tip: '温かいうちにきなこと砂糖をまぶすのが基本。冷やしても食べられる。',
      links: searchLinks('由布市', 'やせうま'),
    },
  },
  {
    id: 'nakatsu-karaage',
    level: 2,
    name: 'からあげ',
    shortName: 'からあげ',
    image: '/assets/foods/oita/02_nakatsu_karaage.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '約10種を合わせた、店ごとのタレ。',
    source: 'https://www.city-nakatsu.jp/doc/2015032900134/',
    icon: { label: 'からあげ', shape: 'dumpling', baseColor: '#d08a3a', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 500,
      area: '中津市',
      headline: '醤油や塩に、にんにく・しょうがなど約10種の調味料を合わせたタレに漬けて揚げる。',
      tip: '注文を受けてから揚げる店が多い。タレの配合と漬けこみ時間は店ごとに違う。',
      links: searchLinks('中津市', 'からあげ'),
    },
  },
  {
    id: 'beppu-reimen',
    level: 3,
    name: '別府冷麺',
    shortName: '別府冷麺',
    image: '/assets/foods/oita/03_beppu_reimen.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'しょうゆベースの冷たいスープに、小麦粉やそば粉を使った歯ごたえのある太めの麺を入れ、キャベツキムチなどをのせる。こんにゃくのようなつるんとした、やや細い麺に白菜キムチを合わせる店もある。',
    source:
      'https://www.visit-oita.jp/gourmets/',
    icon: { label: '別府冷麺', shape: 'noodle-bowl', baseColor: '#8a7a68', accentColor: '#c0392b' },
    reward: {
      unlockScore: 1000,
      area: '別府市',
      headline: '太めの麺を冷たいスープで',
      tip: '冷麺の専門店の系統と、焼肉店の系統の2種類に分かれる。どちらも店ごとに独自の味があり、夏だけでなく1年中食べられる店も少なくない。',
      links: searchLinks('別府市', '別府冷麺'),
    },
  },
  {
    id: 'hita-yakisoba',
    level: 4,
    name: '日田焼きそば',
    shortName: '日田焼きそば',
    image: '/assets/foods/oita/04_hita_yakisoba.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'ゆでた麺を鉄板でしっかりカリカリになるまで焼く。具はもやし・ねぎ・豚肉とシンプルで、麺の香ばしさともやしのしゃきしゃきした歯ざわりを、甘辛いソースとからめて食べる。',
    source:
      'https://www.visit-oita.jp/gourmets/',
    icon: { label: '焼きそば', shape: 'noodle-bowl', baseColor: '#c98a3a', accentColor: '#e8e0c0' },
    reward: {
      unlockScore: 1800,
      area: '日田市',
      headline: '麺を鉄板でカリカリに焼く',
      tip: '日田市のラーメン店には必ずあるといわれる、地元で親しまれてきた味。日田市観光協会も、日田のグルメの中心として紹介している。',
      links: searchLinks('日田市', '日田焼きそば'),
      official: [{ label: '日田市観光協会「食す」', url: 'https://oidehita.com/eat' }],
    },
  },
  {
    id: 'ryukyu',
    level: 5,
    name: 'りゅうきゅう',
    shortName: 'りゅうきゅう',
    image: '/assets/foods/oita/05_ryukyu.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '漁師のまかないが、名物になった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ryukyu_oita.html',
    icon: { label: 'りゅうきゅう', shape: 'fish', baseColor: '#c05a4a', accentColor: '#e8d8b0' },
    reward: {
      unlockScore: 3000,
      area: '県南部沿岸（佐伯市ほか）',
      headline: 'アジやサバの刺身を醤油・酒・みりん・ごまのタレに漬けこむ。100年フード認定。',
      tip: '熱いごはんにのせる食べ方と、お茶漬けにする食べ方がある。',
      links: searchLinks('佐伯市', 'りゅうきゅう'),
    },
  },
  {
    id: 'gomadashi-udon',
    level: 6,
    name: 'ごまだしうどん',
    shortName: 'ごまだし',
    image: '/assets/foods/oita/06_gomadashi_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '湯を注ぐだけで、一杯になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/gomadashi_oita.html',
    icon: { label: 'ごまだし', shape: 'ramen', baseColor: '#e0c070', accentColor: '#8a6a3a' },
    reward: {
      unlockScore: 4500,
      area: '佐伯市',
      headline: '焼いた魚をほぐし、醤油・ごま・みりんを合わせた調味料。100年フード認定。',
      tip: 'ゆでたうどんにのせて湯を注ぐだけ。茶飯やつけ麺のつゆにも使う。',
      links: searchLinks('佐伯市', 'ごまだしうどん'),
    },
  },
  {
    id: 'toriten',
    level: 7,
    name: 'とり天',
    shortName: 'とり天',
    image: '/assets/foods/oita/07_toriten.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '練りからしをそえた、酢醤油で。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/toriten_oita.html',
    icon: { label: 'とり天', shape: 'dumpling', baseColor: '#e0b060', accentColor: '#b07a3a' },
    reward: {
      unlockScore: 6500,
      area: '大分県内全域（大分市・別府市ほか）',
      headline: '下味をつけた鶏肉を天ぷら粉で揚げる。昭和初期から親しまれている。',
      tip: '練りからしをそえた酢醤油で食べるのが一般的。ポン酢やかぼすも合う。',
      links: searchLinks('大分市', 'とり天'),
    },
  },
  {
    id: 'dangojiru',
    level: 8,
    name: 'だんご汁',
    shortName: 'だんご汁',
    image: '/assets/foods/oita/08_dangojiru.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: 'だんごは、丸くない。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/dangojiru_oita.html',
    icon: { label: 'だんご汁', shape: 'pot', baseColor: '#c8a060', accentColor: '#8a6a3a' },
    reward: {
      unlockScore: 9000,
      area: '大分県内全域',
      headline: '小麦粉を手で引きのばした帯状のだんごを汁で煮こむ。里芋との相性がよい。',
      tip: 'きしめん状で、うどんよりコシが強い。味がしみやすいようにこの形にしたとされる。',
      links: searchLinks('大分県', 'だんご汁'),
    },
  },
  {
    id: 'torimeshi',
    level: 9,
    name: '鶏めし',
    shortName: '鶏めし',
    image: '/assets/foods/oita/09_torimeshi.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '人が集まる日の、炊きこみ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/torimeshi_oita.html',
    icon: { label: '鶏めし', shape: 'roe', baseColor: '#c8a060', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 12000,
      area: '大分県内全域（日田市吉野地区ほか）',
      headline: '鶏肉とごぼう・人参を炊きこんだ飯。親戚が集まるときや祭りのハレの料理。',
      tip: '鶏肉とごぼうを醤油と砂糖で煮つめてから飯に混ぜるか、一緒に炊く。',
      links: searchLinks('日田市', '鶏めし'),
    },
  },
  {
    id: 'hyuga-don',
    level: 10,
    name: 'ひゅうが丼',
    shortName: 'ひゅうが丼',
    image: '/assets/foods/oita/10_hyuga_don.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '保戸島の、漁師の丼。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/hyugadon_oita.html',
    icon: { label: 'ひゅうが丼', shape: 'roe', baseColor: '#b8443a', accentColor: '#e8c860' },
    reward: {
      unlockScore: 16000,
      area: '津久見市（保戸島）',
      headline: 'マグロを卵黄入りのごま醤油ダレであえ、熱いご飯にのせる。名の由来は不明とされる。',
      tip: 'いりごまをよくすり、砂糖を加えた少し甘めの醤油ダレに卵黄を入れる。',
      links: searchLinks('津久見市', 'ひゅうが丼'),
    },
  },
  {
    id: 'seki-aji-saba',
    level: 11,
    name: '関あじ・関さば',
    shortName: '関あじ関さば',
    image: '/assets/foods/oita/11_seki_aji_saba.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '速吸瀬戸で釣って、佐賀関に上げる。',
    source: 'https://www.pref.oita.jp/soshiki/10400/tokusanhin-sekiajisekisaba.html',
    icon: { label: '関あじ関さば', shape: 'fish', baseColor: '#8aa8b8', accentColor: '#e0e8ec' },
    reward: {
      unlockScore: 22000,
      area: '大分市佐賀関',
      headline: '佐賀関沖の速吸瀬戸で一本釣りされ、佐賀関に水揚げされたものだけの呼び名。',
      tip: '水揚げ前に包丁で骨を切って血抜きする「活け締め」。氷詰めで5度ほどに保って送られる。',
      links: searchLinks('大分市佐賀関', '関あじ 関さば'),
    },
  },
  {
    id: 'bungo-gyu',
    level: 12,
    name: '豊後牛',
    shortName: '豊後牛',
    image: '/assets/foods/oita/12_bungo_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '大正10年、国の共進会で。',
    source: 'https://www.pref.oita.jp/soshiki/10400/tokusanhin-bungogyuu.html',
    icon: { label: '豊後牛', shape: 'feast', baseColor: '#c0443a', accentColor: '#f0e0d0' },
    reward: {
      unlockScore: 30000,
      area: '竹田市・九重町ほか県内全域',
      headline: '大分県内で育てられた黒毛和種。大正10年に国の共進会で「豊後牛は日本一」と称された。',
      tip: '一定の肉質等級以上のものは「おおいた豊後牛」として区別されている。',
      links: searchLinks('大分県', '豊後牛'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const oitaFoodStage: StageDefinition = {
  regionId: 'oita',
  regionName: '大分県',
  mode: 'food',
  title: '大分めしマージ',
  theme: {
    primaryColor: '#3a6a5a',
    secondaryColor: '#d9a441',
    accentColor: '#e8c86b',
    backgroundColor: '#141a18',
  },
  /** 背景ツアー「大分めぐり」。軸は時間帯。由布院の朝霧から別府の夜へ。 */
  scenery: {
    tour: oitaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, OITA_FOOD_HULLS),
};
